import { useEffect, useRef, useState } from 'react';

import { toApprovalPlan } from 'containers/ApprovalRules/ApprovalsQueue/normalize';
import { ApprovalPlan } from 'containers/ApprovalRules/ApprovalsQueue/types';

import { OfferValuesInput, useApprovalPlanForEdit } from './useOfferActions';

/**
 * How long typing has to pause before the question is asked. Long enough that a
 * figure typed digit by digit asks once, short enough that the answer lands
 * while the person is still looking at the field.
 */
const DRAFT_PLAN_DELAY_MS = 400;

type DraftPayload = Pick<OfferValuesInput, 'field_values' | 'condition_values' | 'edited_keys'>;

export interface DraftApprovalPlan {
  plan: ApprovalPlan | null;
  /**
   * The values on the form are waiting for an answer: from the form's load or a
   * change, through the pause, until the answer or its failure lands. `plan` is
   * null throughout.
   */
  pending: boolean;
}

/**
 * What a submit would do if the edit on the form were saved as it stands, asked
 * while it is not: which levels it asks again and which keep their approval.
 * The approvals review panel and the Send Offer drawer both edit an offer
 * through the same endpoints, so both ask it this one way.
 *
 * Null while a change is waiting to be asked about, and when the question
 * failed: saying nothing beats naming approvals for values the person has since
 * replaced.
 */
export const useDraftApprovalPlan = (
  offerId: string | null,
  payload: DraftPayload | null,
  enabled: boolean,
): DraftApprovalPlan => {
  const [draftPlan, setDraftPlan] = useState<ApprovalPlan | null>(null);
  const [pending, setPending] = useState(false);
  // A cancelled request settles neither way, so only the answer to the latest
  // change ends the wait.
  const request = useApprovalPlanForEdit(offerId ?? '', {
    onSuccess: data => {
      setDraftPlan(toApprovalPlan(data?.plan));
      setPending(false);
    },
    onError: () => {
      setDraftPlan(null);
      setPending(false);
    },
  });
  // Held in a ref because the mutation's identity changes on every render, and
  // the effect below must re-run only when the values do.
  const run = useRef(request.mutation);
  run.current = request.mutation;

  // Keyed as a string, so the effect below asks again only when a value
  // actually changed.
  const payloadKey = payload ? JSON.stringify(payload) : null;

  useEffect(() => {
    setDraftPlan(null);
    setPending(false);
    if (!enabled || !offerId || !payload) return undefined;
    setPending(true);

    let cancel: (() => void) | undefined;
    const timer = setTimeout(() => {
      cancel = run.current(payload) as unknown as () => void;
    }, DRAFT_PLAN_DELAY_MS);
    return () => {
      clearTimeout(timer);
      cancel?.();
    };
    // Keyed on the payload's contents, not its identity: callers rebuild it on
    // every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, offerId, payloadKey]);

  return { plan: draftPlan, pending };
};
